import axios from 'axios';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

// Define the types for critical service
export interface CriticalService {
  service_name: string;
  enabled: boolean;
  priority: number;
  category: string;
  description: string;
  created_by: string;
  created_at?: string;
  updated_at?: string;
}

// Define the response type for fetching critical services
export interface CriticalServicesResponse {
  critical_services: CriticalService[]; // API uses critical_services, not services
  cluster: string;
  count: number;
  timestamp: string;
}

// Define the request type for updating a critical service
export interface UpdateCriticalServiceRequest {
  cluster: string;
  service_name: string;
  enabled: boolean;
  priority?: number;
  category?: string;
  description?: string;
  created_by?: string;
}

// Define the state interface
interface CriticalServicesState {
  criticalServices: CriticalService[];
  cluster: string;
  count: number;
  loading: boolean;
  error: string | null;
}

const initialState: CriticalServicesState = {
  criticalServices: [], // Initialize as an empty array to prevent undefined errors
  cluster: '',
  count: 0,
  loading: false,
  error: null,
};

// API URLs - Use full endpoint like other slices
const CRITICAL_SERVICES_API_URL = 'https://api.status.gridbit.live/api/v1/critical';

// Create async thunk for fetching critical services
export const fetchCriticalServices = createAsyncThunk(
  'criticalServices/fetchServices',
  async (cluster: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${CRITICAL_SERVICES_API_URL}/services/${cluster}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch critical services');
    }
  }
);

// Create async thunk for updating a critical service
export const updateCriticalService = createAsyncThunk(
  'criticalServices/updateService',
  async (request: UpdateCriticalServiceRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(CRITICAL_SERVICES_API_URL, request);
      
      // Refresh the critical services list after update
      await dispatch(fetchCriticalServices(request.cluster));
      
      return {
        data: response.data,
        request
      };
    } catch (error: any) {
      return rejectWithValue({
        error: error.response?.data || error.message || 'Failed to update critical service',
        request
      });
    }
  }
);

// Create the critical services slice
export const criticalServicesSlice = createSlice({
  name: 'criticalServices',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetchCriticalServices
      .addCase(fetchCriticalServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCriticalServices.fulfilled, (state, action: PayloadAction<CriticalServicesResponse>) => {
        state.loading = false;

        // Always update cluster from response
        if (action.payload) {
          state.cluster = action.payload.cluster;
          
          // Handle the response with critical_services field
          if (action.payload.critical_services) {
            // If critical_services is not null, use it
            state.criticalServices = action.payload.critical_services;
            state.count = action.payload.count;
            
          } else {
            // If critical_services is null or missing, clear the array
            state.criticalServices = [];
            state.count = 0;
          }
        } else {
          console.error('Reducer: Invalid payload received');
          state.criticalServices = [];
          state.count = 0;
        }
      })
      .addCase(fetchCriticalServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle updateCriticalService
      .addCase(updateCriticalService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCriticalService.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && action.payload.request) {
          const { request } = action.payload;
          toast.success(request.enabled 
            ? `${request.service_name} marked as critical` 
            : `${request.service_name} removed from critical services`
          );
        } else {
          toast.success('Critical service status updated');
        }
      })
      .addCase(updateCriticalService.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.error || 'Unknown error';
        toast.error(`Critical service update failed: ${state.error}`);
      });
  },
});

export default criticalServicesSlice.reducer;
